import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Save, Check } from 'lucide-react';
import { api } from '../api.js';
import { Button, Card, Spinner, Textarea } from '../components/ui.jsx';

export default function TakeAssessmentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, refetch } = useQuery({ queryKey: ['session', id], queryFn: () => api.getSession(id) });

  const [position, setPosition] = useState(0);
  const [responses, setResponses] = useState({});
  const startTimeRef = useRef(Date.now());

  // Hydrate from saved responses on first load
  useEffect(() => {
    if (data?.session?.responses) {
      const initial = {};
      for (const [qid, r] of Object.entries(data.session.responses)) initial[qid] = r.value;
      setResponses(initial);
      // Resume at first unanswered question
      const firstUnanswered = (data.session.questions || []).findIndex(q => !(q.id in initial));
      setPosition(firstUnanswered === -1 ? data.session.questions.length - 1 : firstUnanswered);
    }
  }, [data]);

  const submitMutation = useMutation({
    mutationFn: ({ question_id, value, value_json, time_spent_seconds }) =>
      api.submitResponse(id, { question_id, ...(value_json !== undefined ? { value_json } : { value }), time_spent_seconds })
  });

  const completeMutation = useMutation({
    mutationFn: () => api.completeSession(id),
    onSuccess: () => navigate(`/results/${id}?just_completed=1`)
  });

  if (isLoading || !data) return <div className="min-h-screen grid place-items-center"><Spinner className="w-8 h-8 text-primary-600" /></div>;
  if (data.session.status === 'completed') {
    navigate(`/results/${id}`);
    return null;
  }

  const session = data.session;
  const questions = session.questions || [];
  const total = questions.length;
  const current = questions[position];
  const answered = Object.keys(responses).length;
  const progress = (answered / total) * 100;

  const setAnswer = async (value, value_json) => {
    const qid = current.id;
    const newResponses = { ...responses, [qid]: value_json !== undefined ? value_json : value };
    setResponses(newResponses);
    const time_spent_seconds = Math.round((Date.now() - startTimeRef.current) / 1000);
    try {
      await submitMutation.mutateAsync({ question_id: qid, value, value_json, time_spent_seconds });
    } catch (err) {
      console.error('save failed', err);
    }
  };

  const next = () => {
    if (position < total - 1) {
      setPosition(p => p + 1);
      startTimeRef.current = Date.now();
    }
  };
  const prev = () => {
    if (position > 0) {
      setPosition(p => p - 1);
      startTimeRef.current = Date.now();
    }
  };

  const allAnswered = answered === total;
  const currentAnswered = current && (current.id in responses);

  return (
    <div className="min-h-screen bg-gradient-to-br from-ink-50 via-white to-primary-50/40">
      <header className="bg-white/80 backdrop-blur border-b border-ink-200/70 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs text-ink-500">{session.module.name}</div>
            <div className="text-sm font-medium text-ink-900 truncate">Question {position + 1} of {total}</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-xs text-ink-500 flex items-center gap-1.5"><Save className="w-3.5 h-3.5" /> Saved automatically</div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/assessments')}>Save & exit</Button>
          </div>
        </div>
        <div className="h-1 bg-ink-100">
          <div className="h-full bg-gradient-to-r from-primary-600 to-accent-600 transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <AnimatePresence mode="wait">
          <motion.div key={current?.id}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}>
            <Card className="p-8">
              <div className="text-xs uppercase tracking-wider font-semibold text-accent-700 mb-2">{current.competency.replace(/_/g, ' ')}</div>
              {current.prompt_context ? (
                <div className="bg-ink-50 border border-ink-200 rounded-lg p-4 mb-4 text-sm text-ink-700 leading-relaxed">{current.prompt_context}</div>
              ) : null}
              <h2 className="text-lg text-ink-900 leading-relaxed mb-6">{current.question_text}</h2>

              <QuestionInput question={current} value={responses[current.id]} onChange={(v, vJson) => setAnswer(v, vJson)} />
            </Card>
          </motion.div>
        </AnimatePresence>

        <div className="mt-6 flex items-center justify-between">
          <Button variant="secondary" onClick={prev} disabled={position === 0}><ChevronLeft className="w-4 h-4 mr-1" />Previous</Button>
          {position < total - 1 ? (
            <Button onClick={next} disabled={!currentAnswered}>Next<ChevronRight className="w-4 h-4 ml-1" /></Button>
          ) : (
            <Button onClick={() => completeMutation.mutate()} loading={completeMutation.isPending} disabled={!allAnswered}>
              <Check className="w-4 h-4 mr-1.5" />Submit assessment
            </Button>
          )}
        </div>

        {!allAnswered && position === total - 1 ? (
          <p className="text-xs text-ink-500 mt-3 text-right">{total - answered} question{total - answered === 1 ? '' : 's'} remaining</p>
        ) : null}
      </main>
    </div>
  );
}

function QuestionInput({ question, value, onChange }) {
  switch (question.question_type) {
    case 'likert':
    case 'validity_check':
      return <LikertInput value={value} onChange={onChange} />;
    case 'multiple_choice':
      return <MultipleChoiceInput question={question} value={value} onChange={onChange} />;
    case 'sjt':
    case 'ranking':
      return <RankingInput question={question} value={value} onChange={onChange} />;
    case 'free_text':
      return <FreeTextInput question={question} value={value} onChange={onChange} />;
    default:
      return <p className="text-sm text-danger">Unknown question type: {question.question_type}</p>;
  }
}

function LikertInput({ value, onChange }) {
  const labels = ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'];
  return (
    <div className="grid grid-cols-5 gap-2">
      {labels.map((label, i) => {
        const v = String(i + 1);
        const selected = String(value) === v;
        return (
          <button key={v} type="button" onClick={() => onChange(v)}
            className={'p-3 rounded-lg border text-center transition-all ' +
              (selected
                ? 'border-primary-600 bg-gradient-to-br from-primary-50 to-accent-50 ring-2 ring-primary-500/30'
                : 'border-ink-200 hover:border-ink-300 hover:bg-ink-50')}>
            <div className={'text-2xl font-semibold mb-1 ' + (selected ? 'text-primary-700' : 'text-ink-400')}>{i + 1}</div>
            <div className={'text-[10px] leading-tight ' + (selected ? 'text-primary-700 font-medium' : 'text-ink-500')}>{label}</div>
          </button>
        );
      })}
    </div>
  );
}

function MultipleChoiceInput({ question, value, onChange }) {
  // Randomise option order per render but stably per question id
  const options = useMemo(() => stableShuffle(question.options || [], question.id), [question]);
  return (
    <div className="space-y-2">
      {options.map(opt => {
        const selected = String(value) === String(opt.index);
        return (
          <button key={opt.index} type="button" onClick={() => onChange(String(opt.index))}
            className={'w-full text-left p-4 rounded-lg border transition-all ' +
              (selected
                ? 'border-primary-600 bg-gradient-to-br from-primary-50 to-accent-50/50 ring-2 ring-primary-500/30'
                : 'border-ink-200 hover:border-ink-300 hover:bg-ink-50')}>
            <div className="flex items-start gap-3">
              <div className={'mt-0.5 w-5 h-5 rounded-full border-2 grid place-items-center shrink-0 ' + (selected ? 'border-primary-600 bg-primary-600' : 'border-ink-300')}>
                {selected ? <Check className="w-3 h-3 text-white" /> : null}
              </div>
              <span className={selected ? 'text-ink-900 font-medium' : 'text-ink-700'}>{opt.text}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function RankingInput({ question, value, onChange }) {
  // Value is an array of option indices in ranked order (most → least)
  const options = useMemo(() => stableShuffle(question.options || [], question.id), [question]);
  const [order, setOrder] = useState(() => Array.isArray(value) ? value : options.map(o => o.index));

  useEffect(() => {
    if (Array.isArray(value) && value.length === options.length) setOrder(value);
  }, [value, options.length]);

  const move = (from, to) => {
    if (to < 0 || to >= order.length) return;
    const newOrder = [...order];
    const [item] = newOrder.splice(from, 1);
    newOrder.splice(to, 0, item);
    setOrder(newOrder);
    onChange(undefined, newOrder);
  };

  const optionByIdx = Object.fromEntries(options.map(o => [o.index, o]));

  return (
    <div className="space-y-2">
      <p className="text-xs text-ink-500 mb-3">Reorder these from <strong>most</strong> like what you'd do (top) to <strong>least</strong> like what you'd do (bottom).</p>
      {order.map((optIdx, position) => {
        const opt = optionByIdx[optIdx];
        return (
          <div key={optIdx} className="flex items-center gap-3 p-3 rounded-lg border border-ink-200 bg-white hover:border-ink-300 transition-all">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary-700 to-accent-600 text-white text-sm font-semibold grid place-items-center shrink-0">{position + 1}</div>
            <div className="flex-1 text-sm text-ink-700">{opt?.text}</div>
            <div className="flex flex-col gap-1">
              <button type="button" onClick={() => move(position, position - 1)} disabled={position === 0}
                className="px-2 py-0.5 text-xs rounded text-ink-600 hover:bg-ink-100 disabled:opacity-30">▲</button>
              <button type="button" onClick={() => move(position, position + 1)} disabled={position === order.length - 1}
                className="px-2 py-0.5 text-xs rounded text-ink-600 hover:bg-ink-100 disabled:opacity-30">▼</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FreeTextInput({ question, value, onChange }) {
  const [local, setLocal] = useState(typeof value === 'string' ? value : '');
  useEffect(() => { setLocal(typeof value === 'string' ? value : ''); }, [question.id]); // eslint-disable-line
  const wordCount = local.trim() ? local.trim().split(/\s+/).length : 0;
  return (
    <div>
      <Textarea value={local} onChange={e => setLocal(e.target.value)}
        onBlur={() => onChange(local)}
        placeholder="Type your answer here. Press Tab or click outside to save."
        className="min-h-[180px]" />
      <p className="text-xs text-ink-400 mt-1.5">{wordCount} words</p>
    </div>
  );
}

function stableShuffle(arr, seed) {
  // Deterministic shuffle so re-renders don't reorder mid-question
  let h = 0;
  for (const ch of String(seed)) h = (h * 31 + ch.charCodeAt(0)) | 0;
  const indexed = arr.map((v, i) => ({ v, k: ((h ^ (i * 2654435761)) >>> 0) }));
  indexed.sort((a, b) => a.k - b.k);
  return indexed.map(x => x.v);
}
